import { abortAssetMultipartUpload, ASSET_THUMBNAILS_QUERY_KEY, completeAssetMultipartUpload, initAssetMultipartUpload, registerBeforeDeleteAssets, uploadAssetMultipartPart, submitAssetFilmstripFrames, } from '@/api/assetApi';
import { measureVideoDurationMs } from '@/entities/asset/model/mediaMetadataProbe';
import { isLocalFilmstripComplete, } from '@/features/editor/model/timelineSnapshotTiles';
import { captureLocalFilmstripFrames, revokeLocalFilmstripFrames, } from '@/features/upload-asset/model/captureLocalFilmstripFrames';
import { captureLocalVideoPoster } from '@/features/upload-asset/model/captureLocalVideoPoster';
import { BACKGROUND_UPLOAD_PART_BYTES, PART_UPLOAD_ATTEMPTS, isSameUploadFile, partRetryDelayMs, planPartRanges, } from '@/features/upload-asset/model/backgroundUploadPlan';
import { readGrantedUploadFile, uploadFileHandleOf, } from '@/features/upload-asset/model/uploadFileHandle';
import { deleteUploadFileHandle, deleteUploadResumeRecord, loadUploadFileHandles, loadUploadResumeRecords, saveUploadFileHandle, saveUploadResumeRecord, } from '@/features/upload-asset/model/uploadResumeStore';
/** 每个素材当前运行的请求控制器，取消时真正停止分片传输。 */
const uploadControllers = new Map();
/** 同一文件同时上传的分片数。 */
const PART_CONCURRENCY = 3;
/** 本地胶片提交失败后的最多尝试次数，含第一次。 */
const FILMSTRIP_SUBMIT_ATTEMPTS = 3;
/** 本地胶片提交失败后的重试间隔，毫秒。 */
const FILMSTRIP_SUBMIT_RETRY_DELAY_MS = 600;
/** 当前页面内的上传任务。 */
const tasks = new Map();
const heldLocalSources = new Map();
/** 每个素材正在进行的本地截帧。换文件或放开预览时用它停掉旧的一轮。 */
const filmstripCaptures = new Map();
/** 本地胶片是否已经提交或正在提交。重新截帧时清掉，允许再交一次。 */
const filmstripSubmitState = new Map();
/** 本次页面内存里的原文件。刷新后清空。 */
const files = new Map();
/** 可跨刷新读取的文件句柄。只在用户点击重试时才读文件。 */
const fileHandles = new Map();
/** 进度变化的订阅者。 */
const listeners = new Set();
/** 用来在上传完成后刷新素材列表。由进度条挂载时注入。 */
let queryClient = null;
/** 是否已经从 IndexedDB 恢复过断点。 */
let restored = false;
/** 时间轴当前选中、且仍在上传的素材。进度面板用它把这一条滚进视野。 */
let focusedAssetId = null;
/**
 * 注入查询客户端，上传完成后刷新素材列表。
 * @param client 应用查询客户端
 */
export function bindBackgroundUploadQueryClient(client) {
    queryClient = client;
}
/**
 * 订阅上传进度。返回取消订阅函数。
 * @param listener 进度变化回调
 * @returns 取消订阅
 */
export function subscribeBackgroundUploads(listener) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}
/** 当前全部未完成任务。 */
export function getBackgroundUploadTasks() {
    return [...tasks.values()].map(toPublicTask);
}
/** 编辑器目录需要覆盖的本地播放地址。 */
export function getLocalUploadSources() {
    /** 还在上传的本地文件。 */
    const uploading = [...tasks.values()]
        .filter((task) => task.localSrc != null)
        .map((task) => ({
        assetId: task.assetId,
        src: task.localSrc ?? '',
        durationMs: task.durationMs,
        posterSrc: task.posterSrc,
        filmstripFrames: task.filmstripFrames,
    }));
    /** 已经传完、胶片图还没显示的本地文件。同一素材以队列里的为准。 */
    const held = [...heldLocalSources.entries()]
        .filter(([assetId]) => !tasks.has(assetId))
        .map(([assetId, source]) => ({
        assetId,
        src: source.src,
        durationMs: source.durationMs,
        posterSrc: source.posterSrc,
        filmstripFrames: source.filmstripFrames,
    }));
    return [...uploading, ...held];
}
/**
 * 胶片图已经显示，或生成失败后，放开本地预览并切回正式地址。
 * @param assetId 素材 ID
 */
export function releaseLocalPreview(assetId) {
    const held = heldLocalSources.get(assetId);
    if (!held)
        return;
    stopFilmstripCapture(assetId);
    heldLocalSources.delete(assetId);
    URL.revokeObjectURL(held.src);
    if (held.posterSrc)
        URL.revokeObjectURL(held.posterSrc);
    revokeLocalFilmstripFrames(held.filmstripFrames);
    notifyUploads();
}
/** 是否还有未传到 OSS 的任务，用于离开页面提示。 */
export function hasUnfinishedBackgroundUploads() {
    return tasks.size > 0;
}
/**
 * 选中时间轴上仍在上传的视频时，让进度面板对准这一条。
 * 素材不在队列里时不改变当前焦点。
 * @param assetId 片段引用的素材 ID
 */
export function focusBackgroundUpload(assetId) {
    if (!tasks.has(assetId))
        return;
    focusedAssetId = assetId;
    notifyUploads();
}
/** 当前要对准的上传素材；没有选中上传中的视频时为空。 */
export function getFocusedBackgroundUploadId() {
    return focusedAssetId != null && tasks.has(focusedAssetId) ? focusedAssetId : null;
}
/**
 * 把占位素材放进后台队列并立即开始上传。
 * @param input 已创建的占位素材和本地文件
 */
export function enqueueBackgroundUpload(input) {
    /** 本次页面用于预览的本地地址。 */
    const localSrc = URL.createObjectURL(input.file);
    /** 新任务。重复入队时先停掉旧循环。 */
    const previous = tasks.get(input.asset.id);
    const task = {
        assetId: input.asset.id,
        projectId: input.asset.projectId ?? null,
        fileName: input.file.name,
        fileSize: input.file.size,
        lastModified: input.file.lastModified,
        loadedBytes: 0,
        status: 'uploading',
        errorMessage: null,
        localSrc,
        durationMs: null,
        posterSrc: null,
        filmstripFrames: [],
        assetDataType: input.assetDataType,
        uploadId: null,
        objectKey: null,
        partSize: BACKGROUND_UPLOAD_PART_BYTES,
        completedParts: [],
        runToken: (previous?.runToken ?? 0) + 1,
        hasFileHandle: false,
    };
    stopFilmstripCapture(input.asset.id);
    filmstripSubmitState.delete(input.asset.id);
    if (previous?.localSrc)
        URL.revokeObjectURL(previous.localSrc);
    if (previous?.posterSrc)
        URL.revokeObjectURL(previous.posterSrc);
    revokeLocalFilmstripFrames(previous?.filmstripFrames ?? []);
    /** 同一素材重新上传时，上一轮留给预览的本地文件不再使用。 */
    const held = heldLocalSources.get(input.asset.id);
    if (held) {
        heldLocalSources.delete(input.asset.id);
        if (held.src !== previous?.localSrc)
            URL.revokeObjectURL(held.src);
        if (held.posterSrc && held.posterSrc !== previous?.posterSrc)
            URL.revokeObjectURL(held.posterSrc);
        revokeLocalFilmstripFrames(held.filmstripFrames);
    }
    tasks.set(task.assetId, task);
    files.set(task.assetId, input.file);
    rememberTaskHandle(task, uploadFileHandleOf(input.file));
    notifyUploads();
    void measureAndStoreDuration(task.assetId, localSrc);
    void runUpload(task.assetId, task.runToken);
}
/**
 * 失败或刷新后继续上传。内存里有文件就直接续传；只有句柄时在这次点击里读回原文件。
 * 句柄失效、权限被拒绝或文件已移动时，改成等待重新选择。
 * @param assetId 占位素材 ID
 */
export function retryBackgroundUpload(assetId) {
    void continueFromAvailableFile(assetId);
}
/**
 * 刷新后用重新选择的文件接上断点。文件身份不一致时不覆盖已完成分片。
 * @param assetId 占位素材 ID
 * @param file 用户重新选择的文件
 */
export function resumeBackgroundUpload(assetId, file) {
    const task = tasks.get(assetId);
    if (!task)
        return;
    if (!isSameUploadFile({ name: task.fileName, size: task.fileSize, lastModified: task.lastModified }, file)) {
        task.errorMessage = '请选择原来的文件才能继续上传';
        notifyUploads();
        return;
    }
    files.set(assetId, file);
    task.localSrc ??= URL.createObjectURL(file);
    task.status = 'uploading';
    task.errorMessage = null;
    task.runToken += 1;
    notifyUploads();
    void measureAndStoreDuration(assetId, task.localSrc);
    void runUpload(assetId, task.runToken);
}
/**
 * 取消上传并中止 OSS 分片。占位素材是否删除由调用方决定。
 * @param assetId 占位素材 ID
 */
export async function cancelBackgroundUpload(assetId) {
    const task = tasks.get(assetId);
    if (!task)
        return;
    task.runToken += 1;
    const uploadId = task.uploadId;
    const objectKey = task.objectKey;
    removeTask(assetId);
    if (uploadId && objectKey) {
        try {
            await abortAssetMultipartUpload({ assetId, uploadId, objectKey });
        }
        catch {
            // 本地任务已经移除；中止失败留给服务端的过期清理。
        }
    }
}
/**
 * 页面打开时恢复未完成断点。只取出句柄，不读取文件内容。
 * 有句柄时等待用户点击重试；没有句柄时等待重新选择原文件。
 */
export async function restoreBackgroundUploads() {
    if (restored)
        return;
    restored = true;
    const [records, handles] = await Promise.all([
        loadUploadResumeRecords(),
        loadUploadFileHandles(),
    ]);
    records.forEach((record) => {
        if (tasks.has(record.assetId))
            return;
        /** 这个任务刷新前保存的句柄；没有时只能重新选择文件。 */
        const handle = handles.get(record.assetId) ?? null;
        if (handle)
            fileHandles.set(record.assetId, handle);
        tasks.set(record.assetId, {
            ...record,
            loadedBytes: bytesOfParts(record.fileSize, record.partSize, record.completedParts),
            status: 'needs-file',
            errorMessage: handle
                ? '页面已刷新，点击重试继续上传'
                : '页面已刷新，请重新选择原来的文件以继续上传',
            localSrc: null,
            durationMs: null,
            posterSrc: null,
            filmstripFrames: [],
            completedParts: [...record.completedParts],
            runToken: 1,
            hasFileHandle: handle != null,
        });
    });
    notifyUploads();
}
/**
 * 用内存文件或已保存句柄继续当前任务。
 * @param assetId 占位素材 ID
 */
async function continueFromAvailableFile(assetId) {
    const task = tasks.get(assetId);
    if (!task || task.status === 'uploading')
        return;
    if (files.has(assetId)) {
        startRun(task);
        return;
    }
    const handle = fileHandles.get(assetId);
    if (!handle) {
        markNeedsReselect(task, '请重新选择原来的文件以继续上传');
        return;
    }
    /** 这次点击占用的循环代号，读文件期间取消后不再写回。 */
    const token = task.runToken + 1;
    task.runToken = token;
    task.status = 'uploading';
    task.errorMessage = null;
    notifyUploads();
    const file = await readGrantedUploadFile(handle);
    if (!isCurrent(assetId, token))
        return;
    const same = file != null &&
        isSameUploadFile({ name: task.fileName, size: task.fileSize, lastModified: task.lastModified }, file);
    if (!file || !same) {
        fileHandles.delete(assetId);
        task.hasFileHandle = false;
        void deleteUploadFileHandle(assetId).catch(() => undefined);
        markNeedsReselect(task, '原文件已变化或无法读取，请重新选择');
        return;
    }
    resumeBackgroundUpload(assetId, file);
}
/** 用内存里的原文件开始新一轮上传。 */
function startRun(task) {
    task.status = 'uploading';
    task.errorMessage = null;
    task.runToken += 1;
    notifyUploads();
    void runUpload(task.assetId, task.runToken);
}
/**
 * 句柄不可用时改成等待重新选择原文件。
 * @param task 当前任务
 * @param message 进度条上的说明
 */
function markNeedsReselect(task, message) {
    task.status = 'needs-file';
    task.hasFileHandle = false;
    task.errorMessage = message;
    notifyUploads();
}
/**
 * 入队时记下句柄。没有句柄则清掉这个素材之前留下的句柄。
 * @param task 刚入队的任务
 * @param handle 本次选择拿到的句柄
 */
function rememberTaskHandle(task, handle) {
    task.hasFileHandle = handle != null;
    if (!handle) {
        fileHandles.delete(task.assetId);
        void deleteUploadFileHandle(task.assetId).catch(() => undefined);
        return;
    }
    fileHandles.set(task.assetId, handle);
    void saveUploadFileHandle(task.assetId, handle).catch(() => undefined);
}
/** 通知订阅者进度变了。 */
function notifyUploads() {
    listeners.forEach((listener) => {
        listener();
    });
}
/**
 * 时长量到后开始截本地胶片。不等待截完，上传同时继续。
 * 重新测时长时先停掉上一轮，避免同一时间点截两遍。
 * @param assetId 占位素材 ID
 * @param src 本次页面的本地播放地址
 * @param durationMs 整段时长，毫秒
 */
function beginFilmstripCapture(assetId, src, durationMs) {
    stopFilmstripCapture(assetId);
    clearFilmstripFrames(assetId);
    /** 这一轮截帧的取消信号。 */
    const controller = new AbortController();
    filmstripCaptures.set(assetId, controller);
    void captureLocalFilmstripFrames({
        src,
        durationMs,
        signal: controller.signal,
        onFrame: (frame) => {
            storeFilmstripFrame(assetId, src, frame);
        },
    })
        .catch(() => undefined)
        .finally(() => {
        if (filmstripCaptures.get(assetId) === controller)
            filmstripCaptures.delete(assetId);
        if (!controller.signal.aborted) {
            void submitLocalFilmstripIfReady(assetId, src);
        }
    });
}
/**
 * 停掉这个素材还在进行的本地截帧。
 * @param assetId 素材 ID
 */
function stopFilmstripCapture(assetId) {
    filmstripCaptures.get(assetId)?.abort();
    filmstripCaptures.delete(assetId);
}
/**
 * 丢掉这个素材已经截出的本地胶片格。
 * 任务和预览可能握着同一份数组，只收回一次。
 * @param assetId 素材 ID
 */
function clearFilmstripFrames(assetId) {
    /** 还在队列里的任务。 */
    const task = tasks.get(assetId);
    /** 上传完成后留下的预览。 */
    const held = heldLocalSources.get(assetId);
    revokeLocalFilmstripFrames([...(task?.filmstripFrames ?? []), ...(held?.filmstripFrames ?? [])]);
    if (task)
        task.filmstripFrames = [];
    if (held)
        held.filmstripFrames = [];
    filmstripSubmitState.delete(assetId);
}
/**
 * 把新截到的一格放进当前还在用的那份本地源。
 * 地址已经换掉或预览已经放开时，马上收回这张图。
 * @param assetId 素材 ID
 * @param src 开始截帧时的本地播放地址
 * @param frame 新截到的一格
 */
function storeFilmstripFrame(assetId, src, frame) {
    /** 还在上传的任务。地址没变才接收这一格。 */
    const task = tasks.get(assetId);
    if (task?.localSrc === src) {
        task.filmstripFrames = [...task.filmstripFrames, frame];
        notifyUploads();
        return;
    }
    /** 上传已经完成、胶片图还没显示的预览。 */
    const held = heldLocalSources.get(assetId);
    if (held?.src === src) {
        held.filmstripFrames = [...held.filmstripFrames, frame];
        notifyUploads();
        return;
    }
    URL.revokeObjectURL(frame.url);
}
/**
 * 帧已经截齐且分片合并完成后，把本地 JPEG 交给服务端拼接。
 * 上传还没完成、帧数不够或已经提交过时直接返回。失败后允许多次重试。
 * @param assetId 素材 ID
 * @param src 开始截帧时的本地播放地址
 */
async function submitLocalFilmstripIfReady(assetId, src) {
    if (filmstripSubmitState.has(assetId) || tasks.has(assetId))
        return;
    /** 上传完成后留下的预览。任务还在队列里时不提交。 */
    const held = heldLocalSources.get(assetId);
    if (held == null)
        return;
    if (held.src !== src || held.durationMs == null)
        return;
    if (!isLocalFilmstripComplete(held.filmstripFrames.length, held.durationMs))
        return;
    /** 按时间从左到右，避免截帧回调乱序。 */
    const frames = [...held.filmstripFrames].sort((left, right) => left.timeMs - right.timeMs);
    filmstripSubmitState.set(assetId, 'running');
    try {
        /** 提交给服务端的 JPEG。顺序与中点一致。 */
        const blobs = await Promise.all(frames.map(async (frame) => {
            const response = await fetch(frame.url);
            if (!response.ok)
                throw new Error('读取本地胶片帧失败');
            return response.blob();
        }));
        await submitFilmstripWithRetry(assetId, blobs);
        filmstripSubmitState.set(assetId, 'done');
        void queryClient?.invalidateQueries({ queryKey: ASSET_THUMBNAILS_QUERY_KEY });
    }
    catch {
        filmstripSubmitState.delete(assetId);
    }
}
/**
 * 提交本地胶片帧，失败后隔一小段再试。
 * @param assetId 素材 ID
 * @param frames 按中点顺序排列的 JPEG
 */
async function submitFilmstripWithRetry(assetId, frames) {
    /** 已经失败的次数。 */
    let failedAttempt = 0;
    while (failedAttempt < FILMSTRIP_SUBMIT_ATTEMPTS) {
        try {
            await submitAssetFilmstripFrames(assetId, frames);
            return;
        }
        catch (error) {
            failedAttempt += 1;
            if (failedAttempt >= FILMSTRIP_SUBMIT_ATTEMPTS)
                throw error;
            await delay(FILMSTRIP_SUBMIT_RETRY_DELAY_MS);
        }
    }
}
/**
 * 测本地视频时长，并在视频上截一帧给素材卡和时间轴。
 * 时长或封面失败都不中断上传。
 * @param assetId 占位素材 ID
 * @param src 本次页面的本地播放地址
 */
async function measureAndStoreDuration(assetId, src) {
    const task = tasks.get(assetId);
    if (task?.localSrc === src && task.assetDataType === '3') {
        /** 本地首帧；截不到时素材卡继续显示待生成。 */
        const posterSrc = await captureLocalVideoPoster(src);
        const current = tasks.get(assetId);
        if (current?.localSrc !== src) {
            if (posterSrc)
                URL.revokeObjectURL(posterSrc);
        }
        else if (posterSrc) {
            if (current.posterSrc)
                URL.revokeObjectURL(current.posterSrc);
            current.posterSrc = posterSrc;
            notifyUploads();
        }
    }
    const durationMs = await measureVideoDurationMs(src);
    const latest = tasks.get(assetId);
    if (latest?.localSrc !== src || durationMs == null)
        return;
    latest.durationMs = durationMs;
    notifyUploads();
    if (latest.assetDataType === '3') {
        beginFilmstripCapture(assetId, src, durationMs);
    }
}
/**
 * 跑完一个任务的初始化、缺片上传和合并。
 * @param assetId 占位素材 ID
 * @param token 本次循环的代号，取消或重试后旧循环直接退出
 */
async function runUpload(assetId, token) {
    const file = files.get(assetId);
    const task = tasks.get(assetId);
    if (!file || task?.runToken !== token)
        return;
    uploadControllers.get(assetId)?.abort();
    const controller = new AbortController();
    uploadControllers.set(assetId, controller);
    /** 合并失败时需要丢掉旧分片任务，单片失败则保留。 */
    let completing = false;
    try {
        if (!task.uploadId || !task.objectKey) {
            const initiated = await initAssetMultipartUpload({
                assetId,
                fileName: file.name,
                fileSize: file.size,
                contentType: file.type || undefined,
            });
            if (!isCurrent(assetId, token)) {
                // 初始化响应返回前用户已取消，回收刚创建的任务；失败交给现有过期清理。
                await abortAssetMultipartUpload({ assetId, ...initiated }).catch(() => undefined);
                return;
            }
            task.uploadId = initiated.uploadId;
            task.objectKey = initiated.objectKey;
            task.partSize = initiated.partSize;
            task.completedParts = [];
            task.loadedBytes = 0;
            await persistTask(task);
        }
        const uploadId = task.uploadId;
        const objectKey = task.objectKey;
        if (!uploadId || !objectKey)
            return;
        const pending = planPartRanges(file.size, task.partSize).filter((part) => !task.completedParts.some((done) => done.partNumber === part.partNumber));
        await runPartPool(pending, async (part) => {
            await withPartRetry(async () => {
                if (!isCurrent(assetId, token))
                    return;
                const uploaded = await uploadAssetMultipartPart({
                    assetId,
                    uploadId,
                    objectKey,
                    partNumber: part.partNumber,
                    file: file.slice(part.start, part.end),
                    signal: controller.signal,
                });
                if (!isCurrent(assetId, token))
                    return;
                task.completedParts = [
                    ...task.completedParts.filter((done) => done.partNumber !== part.partNumber),
                    { partNumber: uploaded.partNumber, etag: uploaded.etag },
                ];
                task.loadedBytes = bytesOfParts(task.fileSize, task.partSize, task.completedParts);
                notifyUploads();
                await persistTask(task);
            });
        });
        if (!isCurrent(assetId, token))
            return;
        completing = true;
        const completed = await completeAssetMultipartUpload({
            assetId,
            uploadId,
            objectKey,
            fileName: file.name,
            fileSize: file.size,
            ...(task.durationMs != null ? { durationMs: task.durationMs } : {}),
            parts: task.completedParts,
        });
        if (!isCurrent(assetId, token))
            return;
        await deleteUploadResumeRecord(assetId);
        task.loadedBytes = file.size;
        notifyUploads();
        try {
            await refreshAssetQueries(completed.id);
        }
        finally {
            removeTask(assetId, { holdLocalPreview: true });
        }
    }
    catch (error) {
        if (!isCurrent(assetId, token))
            return;
        task.status = 'failed';
        task.errorMessage = errorText(error);
        if (completing) {
            task.uploadId = null;
            task.objectKey = null;
            task.completedParts = [];
            task.loadedBytes = 0;
        }
        notifyUploads();
        await persistTask(task).catch(() => undefined);
    }
    finally {
        if (uploadControllers.get(assetId) === controller) {
            uploadControllers.delete(assetId);
        }
    }
}
/**
 * 最多三个分片同时上传。任一失败后不再开始新分片，并把该错误抛出。
 * @param parts 尚未完成的分片
 * @param worker 上传一片
 */
async function runPartPool(parts, worker) {
    /** 下一片下标。 */
    let index = 0;
    /** 池子里第一个失败。 */
    let failure = null;
    async function runNext() {
        while (failure == null && index < parts.length) {
            const part = parts[index];
            index += 1;
            try {
                await worker(part);
            }
            catch (error) {
                failure = error;
            }
        }
    }
    await Promise.all(Array.from({ length: Math.min(PART_CONCURRENCY, parts.length) }, () => runNext()));
    if (failure instanceof Error)
        throw failure;
    if (failure != null)
        throw new Error('分片上传失败，请重试');
}
/**
 * 单片指数退避。最后一次失败把错误抛回任务。
 * @param upload 上传这一片
 */
async function withPartRetry(upload) {
    /** 已经失败的次数。 */
    let failedAttempt = 0;
    while (failedAttempt < PART_UPLOAD_ATTEMPTS) {
        try {
            await upload();
            return;
        }
        catch (error) {
            failedAttempt += 1;
            if (failedAttempt >= PART_UPLOAD_ATTEMPTS)
                throw error;
            await delay(partRetryDelayMs(failedAttempt));
        }
    }
}
/** 循环代号仍对得上当前任务时才继续写进度。 */
function isCurrent(assetId, token) {
    return tasks.get(assetId)?.runToken === token;
}
/**
 * 从队列和断点库移除任务。
 * 视频上传成功后先留着本地文件给预览，等胶片图显示再释放；取消或失败则马上回收。
 * @param assetId 素材 ID
 * @param options.holdLocalPreview 为 true 时把视频的本地地址转给预览，不立即回收
 */
function removeTask(assetId, options) {
    uploadControllers.get(assetId)?.abort();
    uploadControllers.delete(assetId);
    /** 即将移出队列的任务。 */
    const task = tasks.get(assetId);
    /** 本次页面的本地播放地址。 */
    const localSrc = task?.localSrc ?? null;
    /** 本地首帧，文件还没进 OSS 时轨道可以暂时用它。 */
    const posterSrc = task?.posterSrc ?? null;
    /** 已经截出的本地胶片格。取消时收回，上传成功则留给预览。 */
    const filmstripFrames = task?.filmstripFrames ?? [];
    /** 本地测到的时长。上传成功后预览目录继续用它对齐中点。 */
    const durationMs = task?.durationMs ?? null;
    /** 视频上传成功后继续播本地文件，避免和 OSS 截帧抢同一条地址。 */
    const holdLocalPreview = options?.holdLocalPreview === true && task?.assetDataType === '3' && localSrc != null;
    if (focusedAssetId === assetId)
        focusedAssetId = null;
    tasks.delete(assetId);
    files.delete(assetId);
    fileHandles.delete(assetId);
    if (holdLocalPreview && localSrc) {
        const previous = heldLocalSources.get(assetId);
        if (previous && previous.src !== localSrc)
            URL.revokeObjectURL(previous.src);
        if (previous?.posterSrc && previous.posterSrc !== posterSrc)
            URL.revokeObjectURL(previous.posterSrc);
        if (previous)
            revokeLocalFilmstripFrames(previous.filmstripFrames);
        heldLocalSources.set(assetId, {
            src: localSrc,
            posterSrc,
            durationMs,
            filmstripFrames,
        });
        void submitLocalFilmstripIfReady(assetId, localSrc);
    }
    notifyUploads();
    if (!holdLocalPreview) {
        stopFilmstripCapture(assetId);
        if (localSrc || posterSrc || filmstripFrames.length > 0) {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (localSrc)
                        URL.revokeObjectURL(localSrc);
                    if (posterSrc)
                        URL.revokeObjectURL(posterSrc);
                    revokeLocalFilmstripFrames(filmstripFrames);
                });
            });
        }
    }
    void deleteUploadResumeRecord(assetId).catch(() => undefined);
}
/** 把内部任务收成可保存的断点。 */
async function persistTask(task) {
    const record = {
        assetId: task.assetId,
        projectId: task.projectId,
        fileName: task.fileName,
        fileSize: task.fileSize,
        lastModified: task.lastModified,
        assetDataType: task.assetDataType,
        uploadId: task.uploadId,
        objectKey: task.objectKey,
        partSize: task.partSize,
        completedParts: task.completedParts,
    };
    await saveUploadResumeRecord(record);
}
/** 上传成功后刷新会展示这个素材的列表。 */
async function refreshAssetQueries(assetId) {
    if (!queryClient)
        return;
    await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['assets'] }),
        queryClient.invalidateQueries({ queryKey: ['editor-assets'] }),
        queryClient.invalidateQueries({ queryKey: ['project-materials'] }),
        queryClient.invalidateQueries({ queryKey: ['creation-project-assets'] }),
        queryClient.invalidateQueries({ queryKey: ['asset-detail', assetId] }),
    ]);
}
/** 已完成分片对应的字节数，最后一片按实际长度计算。 */
function bytesOfParts(fileSize, partSize, completed) {
    const done = new Set(completed.map((part) => part.partNumber));
    return planPartRanges(fileSize, partSize)
        .filter((part) => done.has(part.partNumber))
        .reduce((total, part) => total + (part.end - part.start), 0);
}
/** 对外隐藏断点字段。 */
function toPublicTask(task) {
    return {
        assetId: task.assetId,
        projectId: task.projectId,
        fileName: task.fileName,
        fileSize: task.fileSize,
        loadedBytes: task.loadedBytes,
        status: task.status,
        errorMessage: task.errorMessage,
        localSrc: task.localSrc,
        durationMs: task.durationMs,
        posterSrc: task.posterSrc,
        canResumeFromHandle: task.hasFileHandle,
    };
}
/** 把未知错误收成进度条能展示的句子。 */
function errorText(error) {
    if (error instanceof Error && error.message.trim())
        return error.message;
    return '上传失败，请重试';
}
/** 重试等待。 */
function delay(ms) {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}
registerBeforeDeleteAssets(async (ids) => {
    await Promise.all(ids.map((assetId) => cancelBackgroundUpload(assetId)));
});
